import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Lock, FileSearch, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Prévisualisation STRICTEMENT LECTURE SEULE du manifeste de nettoyage.
 * - Aucun bouton de suppression
 * - Aucune action d'écriture
 * - Compteurs + échantillon des 50 premiers objets
 * - SHA-256 affiché pour vérification d'intégrité
 */
export default function CleanupManifest() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/cleanup-manifest");
        setData(r.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Impossible de charger le manifeste");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8" data-testid="cleanup-manifest-loading">Chargement du manifeste…</div>;
  if (!data) return (
    <div className="p-8" data-testid="cleanup-manifest-empty">
      <Alert>
        <AlertTitle>Aucun manifeste disponible</AlertTitle>
        <AlertDescription>
          Lancer <code>scripts/prepare_cleanup_manifest.py</code> pour en générer un.
        </AlertDescription>
      </Alert>
    </div>
  );

  const cats = data.categories || {};
  const cnt = data.counters || {};

  return (
    <div className="p-6 space-y-6" data-testid="cleanup-manifest-page">
      <div className="flex items-center gap-3">
        <Lock className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-semibold">Manifeste de nettoyage — lecture seule</h1>
        <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300">
          Prévisualisation administrative
        </Badge>
      </div>

      <Alert className="border-amber-300 bg-amber-50" data-testid="cleanup-readonly-notice">
        <ShieldAlert className="h-4 w-4 text-amber-700" />
        <AlertTitle>Aucune action destructrice disponible</AlertTitle>
        <AlertDescription className="text-sm">
          {data.reminder}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard testId="cleanup-cat-analyses" label="Objets analysés" value={cats.analyses ?? data.total_objects ?? 0} />
        <KpiCard testId="cleanup-cat-preserved" label="Préservés" value={cats.preserved ?? 0} tone="green" />
        <KpiCard testId="cleanup-cat-critical" label="Factures critiques préservées" value={cats.preserved_critical_invoices ?? 0} tone="green" />
        <KpiCard testId="cleanup-cat-origin-test" label="Origine test démontrée" value={cats.test_origin_proven ?? 0} tone="red" />
        <KpiCard testId="cleanup-cat-ambiguous" label="Ambigus (revue manuelle)" value={cats.ambiguous_manual_review ?? 0} tone="amber" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            Intégrité du manifeste
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div><strong>Fichier :</strong> <code data-testid="cleanup-file">{data.file}</code></div>
          <div><strong>Généré :</strong> <span data-testid="cleanup-generated-at">{data.generated_at}</span></div>
          <div><strong>Base cible :</strong> <code data-testid="cleanup-db">{data.db}</code></div>
          <div className="break-all"><strong>SHA-256 :</strong> <code data-testid="cleanup-sha">{data.sha}</code></div>
          <div className="pt-2 text-xs text-slate-600">
            <strong>Compteurs bruts :</strong>{" "}
            {Object.entries(cnt).map(([k, v]) => <span key={k} className="mr-3">{k}=<strong>{v}</strong></span>)}
          </div>
        </CardContent>
      </Card>

      {data.notes && (
        <Card>
          <CardHeader><CardTitle>Politiques de préservation</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {Object.entries(data.notes).map(([k, v]) => (
              <div key={k} className="border-l-4 border-amber-400 pl-3">
                <div className="font-medium capitalize">{k.replace(/_/g, " ")}</div>
                <div className="text-slate-700">{v}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Échantillon d'objets analysés (50 premiers)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="cleanup-objects-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="p-2 text-left">Collection</th>
                <th className="p-2 text-left">ID</th>
                <th className="p-2 text-left">Évidence</th>
                <th className="p-2 text-left">État</th>
                <th className="p-2 text-left">Action proposée</th>
              </tr>
            </thead>
            <tbody>
              {(data.objects_sample || []).map((o, i) => (
                <tr key={i} className="border-b hover:bg-slate-50">
                  <td className="p-2 font-mono">{o.collection}</td>
                  <td className="p-2 font-mono text-slate-600">{(o.id || "").slice(0, 12)}…</td>
                  <td className="p-2 max-w-[300px] truncate" title={o.evidence}>{o.evidence}</td>
                  <td className="p-2">{o.etat_actuel}</td>
                  <td className="p-2">
                    <Badge variant="outline" className={
                      o.action_proposee === "preserve" ? "bg-green-50 text-green-900 border-green-300" :
                      o.action_proposee === "delete"   ? "bg-red-50 text-red-900 border-red-300" :
                      "bg-amber-50 text-amber-900 border-amber-300"
                    }>
                      {o.action_proposee}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, tone, testId }) {
  const toneClass = tone === "green" ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                 : tone === "red"   ? "border-red-300 bg-red-50 text-red-900"
                 : tone === "amber" ? "border-amber-300 bg-amber-50 text-amber-900"
                 : "border-slate-200 bg-white";
  return (
    <div data-testid={testId} className={`rounded-lg border p-4 ${toneClass}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs mt-1 opacity-80">{label}</div>
    </div>
  );
}
